import React from "react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

type BaseEvent = { id: string | number; registered: number; capacity: number }

interface RescheduleDialogProps<TEvent extends BaseEvent> {
  isOpen: boolean
  onClose: () => void
  attendeeName?: string
  events: TEvent[]
  targetEventId: string | null
  setTargetEventId: (id: string | null) => void
  onReschedule: () => void
  formatEventOption: (event: TEvent) => string
  loading?: boolean
}

const RescheduleDialog = <TEvent extends BaseEvent>({
  isOpen,
  onClose,
  attendeeName,
  events,
  targetEventId,
  setTargetEventId,
  onReschedule,
  formatEventOption,
  loading = false,
}: RescheduleDialogProps<TEvent>) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Reschedule Attendee</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <p className="text-sm font-medium mb-1">Attendee</p>
            <p>{attendeeName}</p>
          </div>

          <div>
            <p className="text-sm font-medium mb-1">Select New Event</p>
            <Select
              disabled={loading}
              value={targetEventId || ""}
              onValueChange={(value) => setTargetEventId(value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select an event" />
              </SelectTrigger>
              <SelectContent>
                {events
                  .filter((event) => event.registered < event.capacity) // Only show events with available capacity
                  .map((event) => (
                    <SelectItem key={event.id} value={event.id.toString()}>
                      {formatEventOption(event as TEvent)}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={onReschedule} disabled={!targetEventId || loading}>
            {loading ? "Rescheduling..." : "Confirm Reschedule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default RescheduleDialog
